import { vehicleApi } from "#/api/vehicleApi";
import ActionButton from "#/components/buttons/ActionButton";
import Pagination from "#/components/buttons/Pagination";
import { showSuccessNotification } from "#/components/notifications";
import BaseTable, { type TableSortProps } from "#/components/tables/BaseTable";
import { getRandomColor } from "#/utils/ColorUtils";
import type { Vehicle } from "#/types/vehicle";
import { Badge, Button, Checkbox, Modal, Skeleton, Table, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconAlertTriangle, IconEye, IconPlus, IconTrash } from "@tabler/icons-react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { useClient } from "#/hook/useClient";
import { formatDate, formatTime } from "#/utils/dateTimeUtils";
import { logger } from "#/utils/logger";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { usePermission } from "#/hooks/usePermission";
import { useTableColumns } from "#/hooks/useTableColumns";
import PermissionTooltip from "#/components/ui/PermissionTooltip";

const VehiclesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { client } = useClient();
  const { canWrite, canDelete } = usePermission();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [vehicleToDelete, setVehicleToDelete] = useState<Vehicle>();

  const [sortBy, setSortBy] = useState<string>("updatedAt");

  // Columns come from the shared config filtered by the client's Column
  // Visibility setting; the Actions column is appended by the page.
  const { visible, headers: columnHeaders, sortableColumns, sortKeyAt, indexOf } =
    useTableColumns<"vehicles", Vehicle>("vehicles", {
      id: {
        cellClassName: "min-w-[100px]",
        cell: (item) => item.id ?? "--",
      },
      name: {
        sortKey: "name",
        cellClassName: "min-w-[180px]",
        cell: (item) => item.name ?? "--",
      },
      rfid: {
        sortKey: "rfid",
        cellClassName: "min-w-[150px]",
        cell: (item) =>
          item.rfid_tag?.rfid ? (
            <Badge color={getRandomColor(item.rfid_tag.rfid)} variant="light" size="sm">
              {item.rfid_tag.rfid}
            </Badge>
          ) : (
            "--"
          ),
      },
      vehicle_model: {
        sortKey: "vehicle_model",
        cellClassName: "min-w-[200px]",
        cell: (item) =>
          item.vehicle_model ? `${item.vehicle_model.brand} ${item.vehicle_model.model}` : "--",
      },
      site: {
        sortKey: "site",
        cellClassName: "min-w-[150px]",
        cell: (item) => item.site?.name ?? "--",
      },
      assigned_driver: {
        cellClassName: "min-w-[150px]",
        cell: (item) =>
          item.drivers && item.drivers.length > 0
            ? item.drivers.map((driver) => driver.name).join(", ")
            : (item.assigned_driver ?? "--"),
      },
      maintenance: {
        sortKey: "maintenance",
        cellClassName: "min-w-[120px]",
        cell: (item) => (item.maintenance ? formatDate(item.maintenance, client) : "--"),
      },
      updatedAt: {
        sortKey: "updatedAt",
        cellClassName: "min-w-[180px]",
        cell: (item) =>
          item.updatedAt ? (
            <span>
              {formatDate(item.updatedAt, client)}
              <br />
              {formatTime(item.updatedAt, client)}
            </span>
          ) : (
            "--"
          ),
      },
    });
  const defaultSortIndex = indexOf("updatedAt");

  const {
    data: { items = [], total = 0, per_page = 0 } = {},
    isPending,
    refetch: getVehicles,
  } = useQuery({
    queryKey: ["vehicles", sortBy, page, limit, sortDirection],
    queryFn: async () => {
      try {
        const response = await vehicleApi.getAll({
          limit: limit,
          page: page,
          ...(sortBy ? { sortBy: sortBy } : {}),
          ...(sortDirection ? { order: sortDirection } : {}),
        });
        return {
          items: response.data.data,
          total: response.data.total,
          per_page: response.data.limit,
        };
      } catch {
        throw new Error("No vehicles returned");
      }
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
    retry: false,
  });

  // What goes with the vehicle about to be deleted. Shipments cascade, so the
  // dialog states the count and requires an explicit acknowledgement.
  const deleteTargetId = vehicleToDelete?.id;
  const { data: deletePreview, isPending: deletePreviewPending } = useQuery({
    queryKey: ["vehicleDeletePreview", deleteTargetId],
    queryFn: async () => {
      if (deleteTargetId === undefined) throw new Error("No vehicle selected");
      const response = await vehicleApi.getDeletePreview(deleteTargetId);
      return response.data;
    },
    enabled: deleteModalOpened && deleteTargetId !== undefined,
    staleTime: 0,
  });
  const shipmentCount =
    deletePreview !== undefined && typeof deletePreview.shipments === "number"
      ? deletePreview.shipments
      : 0;
  const hasShipments = shipmentCount > 0;
  const [shipmentsAcknowledged, setShipmentsAcknowledged] = useState<boolean>(false);
  // Start unticked every time the dialog opens or targets another vehicle.
  useEffect(() => {
    setShipmentsAcknowledged(false);
  }, [deleteModalOpened, deleteTargetId]);
  const deleteConfirmDisabled =
    !canDelete() ||
    deletePreviewPending ||
    deletePreview === undefined ||
    (hasShipments && !shipmentsAcknowledged);

  const deleteVehicleMutation = useMutation({
    mutationFn: async (id: number) => {
      return await vehicleApi.delete(id);
    },
    onSuccess: () => {
      showSuccessNotification({
        message: t("vehicles.deleteVehicle.success"),
      });
      closeDeleteModal();
      setVehicleToDelete(undefined);
      void getVehicles();
    },
    onError: (error) => {
      logger.error("deleteVehicle error:", error);
    },
  });

  return (
    <div className="w-full h-full flex flex-col overflow-hidden gap-4">
      <div className="flex items-center justify-between bg-white z-10">
        <Title order={2}>{t("vehicles.title")}</Title>
        <div className="w-fit flex items-center justify-center gap-4">
          <PermissionTooltip disabled={!canWrite()}>
            <Button
              leftSection={<IconPlus size={18} />}
              onClick={() => {
                void navigate("/fleet/vehicles/new");
              }}
              color="black"
              size="sm"
              disabled={!canWrite()}
            >
              {t("vehicles.createNewVehicle")}
            </Button>
          </PermissionTooltip>
          <Pagination
            page={page}
            total={total}
            limit={per_page}
            onChange={setPage}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
          />
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <BaseTable
          headers={[...columnHeaders, t("common.actions")]}
          loading={isPending}
          allowSorting={true}
          sortableColumns={sortableColumns}
          onSortChange={(e: TableSortProps) => {
            const key = sortKeyAt(e.index);
            if (!key) return;
            setPage(1);
            setSortDirection(e.direction);
            setSortBy(key);
          }}
          defaultSort={
            defaultSortIndex !== undefined
              ? { direction: "DESC", index: defaultSortIndex }
              : undefined
          }
          rows={items.map((item) => {
            return (
              <Table.Tr key={item.id}>
                {visible.map((column) => (
                  <Table.Td key={column.key} className={column.cellClassName}>
                    {column.cell(item)}
                  </Table.Td>
                ))}
                <Table.Td style={{ minWidth: '120px' }}>
                  <div className="w-full flex items-center justify-center gap-2">
                    <ActionButton
                      icon={IconEye}
                      variant="outline"
                      onClick={() => {
                        void navigate(`/fleet/vehicles/${item.id}`);
                      }}
                    />
                    <PermissionTooltip disabled={!canDelete()}>
                      <ActionButton
                        icon={IconTrash}
                        variant="outline"
                        color="red"
                        onClick={() => {
                          setVehicleToDelete(item);
                          openDeleteModal();
                        }}
                        disabled={!canDelete()}
                      />
                    </PermissionTooltip>
                  </div>
                </Table.Td>
              </Table.Tr>
            );
          })}
        />
      </div>

      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        centered
        size="lg"
        title={t("vehicles.deleteVehicle.title")}
        closeOnClickOutside={false}
        classNames={{
          title: "font-bold",
        }}
      >
        <div className="w-full flex flex-col gap-5">
          <div className="flex flex-col gap-3 text-sm">
            <span>{t("vehicles.deleteVehicle.description")}</span>
            <div className="flex flex-col gap-1 bg-slate-100 border border-dashed border-slate-400 rounded-lg p-4 w-full text-center">
              <span className="font-bold">{vehicleToDelete?.name ?? "--"}</span>
              <span className="text-xs text-gray-600">
                {t("vehicles.deleteVehicle.rfid")} {vehicleToDelete?.rfid_tag?.rfid ?? "--"}
              </span>
              {vehicleToDelete?.vehicle_model && (
                <span className="text-xs text-gray-600">
                  {t("vehicles.deleteVehicle.model")} {vehicleToDelete.vehicle_model.brand}{" "}
                  {vehicleToDelete.vehicle_model.model}
                </span>
              )}
            </div>
            {deletePreviewPending ? (
              <Skeleton height={96} radius="sm" />
            ) : hasShipments ? (
              <div className="flex flex-col gap-3 rounded-md border border-red-200 bg-red-50 p-4 text-red-800">
                <div className="flex items-start gap-2">
                  <IconAlertTriangle size={18} className="mt-0.5 shrink-0" />
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold">
                      {t("vehicles.deleteVehicle.shipments", { count: shipmentCount })}
                    </span>
                    <span>{t("vehicles.deleteVehicle.warning")}</span>
                  </div>
                </div>
                <Checkbox
                  color="red"
                  checked={shipmentsAcknowledged}
                  onChange={(event) => setShipmentsAcknowledged(event.currentTarget.checked)}
                  label={t("vehicles.deleteVehicle.acknowledge")}
                  classNames={{ label: "text-red-800" }}
                />
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <span>{t("vehicles.deleteVehicle.warning")}</span>
                <span className="font-bold">{t("vehicles.deleteVehicle.confirmation")}</span>
              </div>
            )}
          </div>

          <div className="w-full grid grid-cols-[auto_1fr] items-center gap-2">
            <Button size="sm" color="black" onClick={closeDeleteModal}>
              {t("common.cancel")}
            </Button>
            <PermissionTooltip disabled={!canDelete()}>
              <Button
                onClick={() => {
                  if (vehicleToDelete) {
                    deleteVehicleMutation.mutate(vehicleToDelete.id);
                  }
                }}
                color="red"
                size="sm"
                loading={deleteVehicleMutation.isPending}
                leftSection={<IconTrash size={18} />}
                disabled={deleteConfirmDisabled}
              >
                {t("vehicles.deleteVehicle.confirmButton")}
              </Button>
            </PermissionTooltip>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default VehiclesPage;
